import { motion } from "framer-motion";
import { Database, Activity, Workflow, Bot, Cpu, ArrowRight, ShieldCheck, Eye, Zap, ScanLine, MonitorSmartphone, Radio } from "lucide-react";
import { PageHero, CTASection } from "../components/site/Shared";
import { Reveal, Stagger, StaggerItem } from "../components/site/Reveal";
import { AhiRadial, MiniArea } from "../components/site/Mockups";

const progression = [
  { icon: Database, tone: "#5B7CFA", title: "Data", body: "PARTECON first creates a real, structured operational data layer — captured at the point where school activity actually happens." },
  { icon: Activity, tone: "#6366F1", title: "Real-Time Operations", body: "That live data powers real-time visibility across classes, attendance, execution, communication and performance." },
  { icon: Workflow, tone: "#8B5CF6", title: "Automation", body: "A connected operating layer enables automation of routine workflows, follow-ups, alerts and exception handling." },
  { icon: Bot, tone: "#22C55E", title: "AI Intelligence", body: "Accumulated, structured operational data becomes the foundation for intelligence — patterns, early signals and decision support." },
  { icon: Cpu, tone: "#F5B93C", title: "Smart School Infrastructure", body: "The same operating layer can progressively connect to smart school infrastructure — one environment, continuously extending." },
];

export default function Intelligence() {
  return (
    <>
      <PageHero
        overline="Intelligence"
        title="From data to intelligence — on one operating layer."
        sub="PARTECON is designed as extensible infrastructure. It creates the operational data layer first, then progressively enables operations, automation, intelligence and smart infrastructure."
      />

      {/* Progression band */}
      <section className="chrome py-16 border-b border-white/8" data-testid="intelligence-progression">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {progression.map((p, i) => (
              <div key={p.title} className="flex items-center gap-4 md:flex-col md:text-center md:gap-3 flex-1">
                <div className="flex md:flex-col items-center gap-4 md:gap-3">
                  <span className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${p.tone}22` }}>
                    <p.icon size={22} style={{ color: p.tone }} />
                  </span>
                  <span className="text-white font-display font-semibold text-[15px]">{p.title}</span>
                </div>
                {i < progression.length - 1 && <ArrowRight className="hidden md:block text-white/25 mx-auto" size={18} />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed stages */}
      <section className="canvas py-20 md:py-28" data-testid="intelligence-stages">
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <span className="overline">The architecture</span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-4 text-[#1A1D26] leading-tight">
              Each layer is built on the one before it.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-4 mt-12">
            {progression.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.05}>
                <div className="rounded-2xl bg-white border border-[#ECECE8] p-6 md:p-8 h-full card-hover">
                  <div className="flex items-center gap-3">
                    <span className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: `${p.tone}18` }}>
                      <p.icon size={20} style={{ color: p.tone }} />
                    </span>
                    <span className="font-mono-accent text-xs text-[#9AA1AD]">0{i + 1}</span>
                  </div>
                  <h3 className="font-display text-xl font-semibold text-[#1A1D26] mt-5">{p.title}</h3>
                  <p className="text-[#6B7280] text-[15px] leading-relaxed mt-2.5">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Signals -> health */}
      <section className="chrome py-20 md:py-28" data-testid="intelligence-ahi">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <span className="overline overline-light">Operational intelligence</span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-4 text-white leading-tight">
              From daily signals to early, actionable intelligence.
            </h2>
            <p className="text-white/60 text-base md:text-lg leading-relaxed mt-5">
              The Analytical Health Index brings attendance, execution, homework and behaviour signals
              together — for visibility, early identification and actionable awareness at school and class level.
            </p>
            <Stagger className="grid sm:grid-cols-3 gap-3 mt-8">
              {[{ icon: Eye, t: "Visibility" }, { icon: ShieldCheck, t: "Early identification" }, { icon: Zap, t: "Actionable insight" }].map((x) => (
                <StaggerItem key={x.t}>
                  <div className="glass rounded-xl p-4">
                    <x.icon size={18} className="text-[#A5B4FC]" />
                    <p className="text-white/85 text-sm font-medium mt-3">{x.t}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-2xl bg-[#0B1240] p-7 border border-white/5" data-testid="ahi-illustrative">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] font-mono-accent uppercase tracking-[0.18em] text-[#F1BF26]/80">Illustrative AHI view</span>
                <span className="text-[10px] font-mono-accent uppercase tracking-widest text-white/40">Example</span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white/50 text-xs">AHI Score</div>
                  <div className="font-mono-accent text-5xl font-semibold text-white mt-1">85.6</div>
                  <div className="flex items-center gap-1.5 mt-2 text-[#22C55E] text-sm">Good Health</div>
                </div>
                <AhiRadial size={150} />
              </div>
              <div className="mt-6 rounded-xl bg-white/5 border border-white/10 p-4">
                <div className="text-white/60 text-xs mb-3">School-wide teaching adherence · illustrative</div>
                <MiniArea color="#F1BF26" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="canvas py-20 md:py-28" data-testid="intelligence-smart-infra">
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#F1BF26]/40 bg-[#F1BF26]/10 px-3 py-1.5 text-[10px] font-mono-accent uppercase tracking-[0.18em] text-[#8A6A00]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#F1BF26]" /> Roadmap · Conceptual architecture
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-5 text-[#1A1D26] leading-tight">Smart School Infrastructure — the natural extension.</h2>
            <p className="text-[#6B7280] text-base md:text-lg leading-relaxed mt-5">The same operating layer is designed to progressively connect to the physical school environment. The capabilities below describe the intended future extension of PARTECON — not currently deployed infrastructure.</p>
          </Reveal>
          <div className="relative mt-12">
            <div className="hidden md:block absolute top-[46px] left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-[#C7CBFB] to-transparent" />
            <Stagger className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: ScanLine, t: "Smart Attendance", d: "Automated, connected capture at the point of entry." },
                { icon: Radio, t: "Connected Campus", d: "School-wide operational systems on one layer." },
                { icon: MonitorSmartphone, t: "Digital Displays", d: "Live school information surfaced across campus." },
                { icon: Cpu, t: "Smart Hardware", d: "Devices that feed and act on the operating layer." },
              ].map((x) => (
                <StaggerItem key={x.t}>
                  <div className="relative rounded-2xl bg-white border border-[#ECECE8] p-6 h-full card-hover text-center md:text-left">
                    <span className="relative z-10 h-11 w-11 rounded-xl bg-[#EEF0FB] flex items-center justify-center mx-auto md:mx-0"><x.icon size={20} className="text-[#6366F1]" /></span>
                    <h3 className="font-display font-semibold text-[#1A1D26] mt-4 text-[15px]">{x.t}</h3>
                    <p className="text-[#6B7280] text-[13px] mt-1.5 leading-relaxed">{x.d}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
          <p className="text-center text-[#9AA1AD] text-xs mt-8 font-mono-accent">Conceptual — the roadmap extension of the PARTECON operating layer</p>
        </div>
      </section>

    </>
  );
}
